import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Button, Table } from "antd";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { journalsQueries } from "@/entities/journal";
import { SelectModal } from "@/features/select-modal";
import { useCoordinator } from "@/shared/lib/hooks";
import { queryClient } from "@/shared/lib/react-query";
import { fallbackString, formatDate, useDebounce } from "@/shared/utils";
import type { components } from "@entities/journal";

interface WorkItem {
  id: string;
  itemNoVersionId: string;
  section3ListId: string;
  selected: boolean;
  workDate: string;
  workType: string;
  workName: string;
  workCondition: string;
}

const getWorks = (apiData: {
  total?: number;
  page?: number;
  limit?: number;
  section3List?: components["schemas"]["SectionThreeItemForListResponse"][];
  permissions?: components["schemas"]["AclPermissionsInner"][];
}): WorkItem[] => {
  if (!apiData?.section3List) return [];

  return (
    apiData?.section3List?.map((item) => ({
      id: item.itemNoVersionHistoryId || "",
      itemNoVersionId: item.itemNoVersionId || "",
      section3ListId: "",
      selected: true,
      workDate: item.workDate || "",
      workType: item.workType === "WORK" ? "Работа" : "Испытание",
      workName: item.workName || "",
      workCondition: item.locationDescription || "",
    })) || []
  );
};

interface SectionCompletedWorksProps {
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  fieldName: string;
}

export const SectionCompletedWorks = ({
  control,
  setValue,
  fieldName,
}: SectionCompletedWorksProps) => {
  const { mainJournalId } = useCoordinator();

  const { data: mainJournalData } = useQuery(
    journalsQueries.getJournal({ mainJournalId }),
  );

  const [selectedStatuses] = useState<string[]>([]);
  const [search] = useState("");
  const [dateStart] = useState<string>("");
  const [dateEnd] = useState<string>("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [modalVisible, setModalVisible] = useState(false);

  const debouncedSearch = useDebounce(search, 300);

  const { data: sectionData } = useQuery(
    journalsQueries.getSection3({
      sectionThreeListId:
        mainJournalData?.mainJournal?.section3?.sectionListId || "",
      search: debouncedSearch,
      page,
      limit,
      status: selectedStatuses.join(","),
      dateFrom: dateStart,
      dateTo: dateEnd,
    }),
  );

  const currentData = useWatch({ control, name: fieldName }) || [];


  const mappedData = useMemo(() => getWorks(sectionData || {}), [sectionData]);

  const tableColumns = [
    {
      title: "Тип работы",
      dataIndex: "workType",
      key: "workType",
      render: (workType: string) => {
        if (workType === "WORK" || workType === "TEST") {
          return workType === "WORK" ? "Работа" : "Испытание";
        }
        return fallbackString(workType);
      },
    },
    {
      title: "Дата выполнения работ",
      dataIndex: "workDate",
      key: "workDate",
      render: (date: string) => fallbackString(formatDate(date)),
    },
    {
      title: "Наименование работ",
      dataIndex: "workName",
      key: "workName",
    },
    {
      title: "Местоположение",
      dataIndex: "workCondition",
      key: "workCondition",
      render: (workCondition: string) => fallbackString(workCondition),
    },
  ];

  const onConfirm = (selected: any[]) => {
    const selectedIds = selected.map((s) => s.id); // ❗ id, не key
    const updated = currentData.filter(
      (item: any) => selectedIds.includes(item.id), // ❗ id
    );

    selected.forEach((sel) => {
      if (!updated.find((item: any) => item.id === sel.id)) {
        updated.push(sel);
      }
    });

    setValue(fieldName, updated);
    setModalVisible(false);
  };

  const handleSelectWorks = () => {
    setModalVisible(true);
    queryClient.invalidateQueries({
      queryKey: journalsQueries.getSection3({
        sectionThreeListId:
          mainJournalData?.mainJournal?.section3?.sectionListId || "",
        search: debouncedSearch,
        page,
        limit,
      }).queryKey,
    });
  };

  return (
    <div className="journal-completed-works">
      <div className="journal-completed-works__header">
        <span>Выполненные работы (необязательно)</span>
        {currentData.length === 0 ? (
          <Button
            style={{ backgroundColor: "rgba(234, 238, 248, 1)" }}
            onClick={handleSelectWorks}
          >
            Добавить
          </Button>
        ) : (
          <Button type="link" onClick={handleSelectWorks}>
            Изменить
          </Button>
        )}
      </div>

      {currentData.length > 0 && (
        <Table
          style={{ marginTop: 16 }}
          dataSource={currentData}
          columns={tableColumns}
          rowKey="id"
          pagination={false}
        />
      )}

      <SelectModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onConfirm={onConfirm}
        data={mappedData}
        columns={tableColumns}
        rowKey="id"
        getRowSearchText={(row) => `${row?.workName} ${row?.workType}`}
        title="Выберите работы"
        filters={null}
        initialSelectedKeys={currentData.map((item: any) => item.id)}
        page={page}
        limit={limit}
        setPage={setPage}
        setLimit={setLimit}
      />
    </div>
  );
};
